import { Injectable } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { ErrorCommunicationService } from './error-communication.service';
import { environment } from '../../environments/environment';
import { CrudService } from '../services/crud-app.service';

interface EditorReference {
  getQuill(): any;
}

/**
 * Service to handle image insertion and validation in Quill editors
 * Supports toolbar button clicks, paste events, and drag-drop
 */
@Injectable({
  providedIn: 'root',
})
export class EditorImageHandlerService {
  private readonly MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB limit
  private readonly MAX_FILE_SIZE_MB = 20; // For error messages

  constructor(
    private errorCommunicationService: ErrorCommunicationService,
    private crud: CrudService,
  ) {}

  /**
   * Initialize image handlers for a Quill editor
   * @param editor - Editor reference with getQuill() method
   * @param changeDetectorRef - Angular ChangeDetectorRef for the component
   */
  initializeImageHandlers(editor: EditorReference, changeDetectorRef: ChangeDetectorRef): void {
    setTimeout(() => {
      if (!editor?.getQuill) {
        return;
      }

      const quill = editor.getQuill();
      const toolbar = quill.getModule('toolbar');

      if (toolbar) {
        // Set up toolbar image button handler
        toolbar.addHandler('image', this.createImageHandler(editor));
      }

      // Set up paste event listener
      quill.root.addEventListener('paste', (e: ClipboardEvent) => this.handlePaste(e, quill));

      // Set up drag-drop event listener
      quill.root.addEventListener('drop', (e: DragEvent) => this.handleDrop(e, quill));

      changeDetectorRef.detectChanges();
    });
  }

  /**
   * Create a handler function for toolbar image button
   * @param editor - Editor reference
   * @returns Handler function for toolbar
   */
  private createImageHandler(editor: EditorReference): () => void {
    return () => {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.click();

      input.onchange = () => {
        const file = input.files && input.files[0];
        if (!file) return;

        if (!this.validateFile(file)) {
          return;
        }

        this.insertImageFromFile(file, editor);
      };
    };
  }

  /**
   * Handle paste event for images
   */
  private handlePaste(e: ClipboardEvent, quill: any): void {
    const clipboard = e.clipboardData;
    if (!clipboard) {
      return;
    }

    for (let i = 0; i < clipboard.items.length; i++) {
      const item = clipboard.items[i];
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (!file) {
          continue;
        }

        if (!this.validateFile(file)) {
          e.preventDefault();
          return;
        }

        this.insertImageFromFile(file, { getQuill: () => quill });
        e.preventDefault();
      }
    }
  }

  /**
   * Handle drag-drop event for images
   */
  private handleDrop(e: DragEvent, quill: any): void {
    const dt = e.dataTransfer;
    if (!dt || !dt.files || dt.files.length === 0) {
      return;
    }

    const file = dt.files[0];
    if (file.type.indexOf('image') === -1) {
      return;
    }

    if (!this.validateFile(file)) {
      e.preventDefault();
      return;
    }

    this.insertImageFromFile(file, { getQuill: () => quill });
    e.preventDefault();
  }

  /**
   * Validate file size and type
   */
  private validateFile(file: File): boolean {
    if (file.size > this.MAX_FILE_SIZE) {
      this.showSizeError();
      return false;
    }

    if (file.type.indexOf('image') === -1) {
      this.errorCommunicationService.showError('Please select a valid image file.');
      return false;
    }

    return true;
  }

  /**
   * Insert image from file into editor
   */
  private insertImageFromFile(file: File, editor: EditorReference): void {
    const reader = new FileReader();
    reader.onload = (ev: any) => {
      const quill = editor.getQuill();
      const range = quill.getSelection(true) || { index: 0 };
      quill.insertEmbed(range.index, 'image', ev.target.result, 'user');
      quill.setSelection((range.index || 0) + 1);
    };
    reader.readAsDataURL(file);
  }

  /**
   * Display file size error message
   */
  private showSizeError(): void {
    this.errorCommunicationService.showError(
      `Image exceeds ${this.MAX_FILE_SIZE_MB}MB. Please choose a smaller image.`,
    );
  }

  /**
   * Validate uploaded image (for onUpload event)
   */
  validateUploadedImage(event: any): void {
    if (!event || !event.files || event.files.length === 0) {
      return;
    }

    const file = event.files[0];
    if (file.size > this.MAX_FILE_SIZE) {
      if (event.reject) {
        event.reject();
      }
      this.showSizeError();
    }
  }

  /**
   * Processes all base64 images in HTML content and uploads them to the server
   * @param htmlContent The HTML content containing img tags with base64 data
   * @returns The HTML content with base64 images replaced by server URLs
   */
  async uploadBase64Images(htmlContent: string): Promise<string> {
    if (!htmlContent) return htmlContent;

    const imgRegex = /<img[^>]+src="([^"]+)"[^>]*>/g;
    const matches = [...htmlContent.matchAll(imgRegex)];
    const processedUrls = new Set<string>();

    let result = htmlContent;

    for (const match of matches) {
      const src = match[1];

      // Skip if already processed
      if (processedUrls.has(src)) {
        continue;
      }

      // Only process base64 images, skip server URLs
      if (src.startsWith('data:image/')) {
        const serverUrl = await this.uploadImage(src);
        // Use replaceAll to replace ALL occurrences of this base64 URL
        result = result.replaceAll(src, serverUrl);
      }

      processedUrls.add(src);
    }

    return result;
  }

  async uploadImage(src: string): Promise<string> {
    // Extract the mime type and base64 data
    const matches = src.match(/^data:(image\/\w+);base64,(.+)$/);
    if (!matches) {
      console.error('Invalid base64 image format');
      return src;
    }

    const mimeType = matches[1]; // e.g., 'image/png', 'image/jpeg'
    const base64Data = matches[2];

    // Convert base64 to binary
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    // Create FormData and append the blob
    const formData = new FormData();
    formData.append('file', blob, 'image.' + mimeType.split('/')[1]); // e.g., 'image.png'

    // Upload using FormData
    const response = await this.crud.post('rte-images/upload', formData);
    //pick base url from environment file
    const baseUrl = environment.apiUrl;
    const url = baseUrl + 'rte-images/resource/' + response.id + '/download';
    return url;
  }
}
